// src/pages/sm/HolidayCalendar.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Stack, Tooltip, Chip,
    Fade, Paper, Switch, Card
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Public as PublicIcon,
    Business as BusinessIcon,
    Star as SpecialIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { 
    NextPlan as NextPlanIcon,
    CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import AppHeader from '../../components/AppHeader';

const HolidayCalendar = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const [holidays, setHolidays] = useState([]);
    const [, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingHoliday, setEditingHoliday] = useState(null);

    const [formData, setFormData] = useState({
        holiday_date: '',
        holiday_name: '',
        holiday_type: 'PUBLIC',
        is_active: 1
    });

    const fetchHolidays = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/holidays`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                setHolidays(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching holidays:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHolidays();
    }, [fetchHolidays]);

    const handleOpenDialog = useCallback((holiday = null) => {
        if (holiday) {
            setEditingHoliday(holiday);
            setFormData({
                holiday_date: holiday.holiday_date.substring(0, 10),
                holiday_name: holiday.holiday_name,
                holiday_type: holiday.holiday_type,
                is_active: holiday.is_active
            });
        } else {
            setEditingHoliday(null);
            setFormData({
                holiday_date: new Date().toISOString().substring(0, 10),
                holiday_name: '',
                holiday_type: 'PUBLIC',
                is_active: 1
            });
        }
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setEditingHoliday(null);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = useCallback(async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL1}/sm/holidays${editingHoliday ? `/${editingHoliday.id}` : ''}`;
            const method = editingHoliday ? 'put' : 'post';

            const res = await axios[method](url, formData, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });

            if (res.data.success) {
                fetchHolidays();
                handleCloseDialog();
            }
        } catch (err) {
            console.error("Error saving holiday:", err);
            alert(err.response?.data?.message || "Error saving holiday");
        }
    }, [editingHoliday, formData, fetchHolidays, handleCloseDialog]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm("Are you sure you want to delete this holiday?")) return;
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/holidays/${id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                fetchHolidays();
            }
        } catch (err) {
            console.error("Error deleting holiday:", err);
            alert(err.response?.data?.message || "Error deleting holiday");
        }
    }, [fetchHolidays]);

    const columnDefs = useMemo(() => [
        {
            headerName: "Date",
            field: "holiday_date",
            width: 150,
            valueFormatter: params => params.value ? new Date(params.value).toLocaleDateString('en-GB') : '',
            cellStyle: { fontWeight: 'bold' }
        },
        { headerName: "Holiday Name", field: "holiday_name", flex: 1 },
        {
            headerName: "Type",
            field: "holiday_type",
            width: 150,
            cellRenderer: params => {
                const types = {
                    'PUBLIC': { icon: <PublicIcon fontSize="inherit" />, color: 'primary.main', bg: '#f5f3ff', border: '#ddd6fe' },
                    'COMPANY': { icon: <BusinessIcon fontSize="inherit" />, color: '#10b981', bg: '#ecfdf5', border: '#d1fae5' },
                    'SPECIAL': { icon: <SpecialIcon fontSize="inherit" />, color: '#f59e0b', bg: '#fffbeb', border: '#fef3c7' }
                };
                const t = types[params.value] || types['PUBLIC'];
                return (
                    <Chip
                        icon={t.icon} label={params.value} size="small"
                        sx={{ 
                            fontWeight: 800, fontSize: 10, bgcolor: t.bg, color: t.color, 
                            border: '1px solid', borderColor: t.border
                        }}
                    />
                );
            }
        },
        {
            headerName: "Status",
            field: "is_active",
            width: 120,
            cellRenderer: params => (
                <Chip 
                    label={params.value === 1 ? "LIVE" : "DRAFT"} size="small"
                    sx={{ 
                        fontWeight: 800, fontSize: 10, 
                        bgcolor: params.value === 1 ? '#f0fdf4' : '#f8fafc',
                        color: params.value === 1 ? '#16a34a' : '#64748b',
                        border: '1px solid', borderColor: params.value === 1 ? '#bbf7d0' : '#e2e8f0'
                    }}
                />
            )
        },
        {
            headerName: "Actions",
            width: 120,
            pinned: 'right',
            cellRenderer: params => (
                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                    <Tooltip title="Edit Holiday">
                        <IconButton size="small" onClick={() => handleOpenDialog(params.data)} sx={{ color: 'primary.main', '&:hover': { bgcolor: '#f5f3ff' } }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(params.data.id)} sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )
        }
    ], [handleDelete, handleOpenDialog]);

    const handleLogout = () => {
        localStorage.clear();
        setIsLoggedIn(false);
        navigate('/login');
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppHeader onLogout={handleLogout} />

            <Box sx={{ p: 4, maxWidth: 1600, mx: 'auto', width: '100%', flexGrow: 1 }}>
                {(() => {
                    const sorted = [...holidays].filter(h => h.is_active && new Date(h.holiday_date) >= new Date()).sort((a,b) => new Date(a.holiday_date) - new Date(b.holiday_date));
                    const next = sorted[0];
                    return (
                        <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                            <Card sx={{ p: 2, flex: 1, borderRadius: 4, display: 'flex', alignItems: 'center', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)', border: '1px solid #f1f5f9' }}>
                                <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#f5f3ff', mr: 2, display: 'flex' }}><CalendarTodayIcon sx={{ color: 'primary.main' }} /></Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Annual Total</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{holidays.filter(h => h.is_active).length}</Typography>
                                </Box>
                            </Card>
                            <Card sx={{ p: 2, flex: 1.5, borderRadius: 4, display: 'flex', alignItems: 'center', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)', border: '1px solid #f1f5f9' }}>
                                <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#ecfdf5', mr: 2, display: 'flex' }}><NextPlanIcon sx={{ color: '#10b981' }} /></Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Closest Upcoming</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {next ? `${next.holiday_name} (${new Date(next.holiday_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})` : 'No upcoming events'}
                                    </Typography>
                                </Box>
                            </Card>
                        </Stack>
                    );
                })()}

                <Fade in timeout={500}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>
                                    Staff Holidays
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                    Manage public holidays and special company closures
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{
                                    bgcolor: 'primary.main', borderRadius: 2.5, px: 3, py: 1.2, fontWeight: 700, textTransform: 'none',
                                    boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }}
                            >
                                Define New Holiday
                            </Button>
                        </Stack>

                        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)', border: '1px solid #f1f5f9' }}>
                            <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 280px)', width: '100%' }}>
                                <AgGridReact
                                    rowData={holidays}
                                    columnDefs={columnDefs}
                                    defaultColDef={{
                                        sortable: true,
                                        filter: true,
                                        resizable: true,
                                        cellStyle: { display: 'flex', alignItems: 'center', color: '#334155', fontWeight: 500 }
                                    }}
                                    pagination={true}
                                    paginationPageSize={15}
                                    animateRows={true}
                                    headerHeight={52}
                                    rowHeight={52}
                                />
                            </div>
                        </Paper>
                    </Box>
                </Fade>
            </Box>

            {/* HOLIDAY DIALOG */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="xs"
                fullWidth
                TransitionComponent={Fade}
                PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', p: 3, pb: 1 }}>
                    {editingHoliday ? "Modify Holiday Period" : "Register Holiday"}
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 1 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Calendar Date</Typography>
                            <TextField
                                fullWidth type="date" name="holiday_date" value={formData.holiday_date} onChange={handleChange}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } } 
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Holiday Designation</Typography>
                            <TextField
                                fullWidth name="holiday_name" placeholder="e.g. Workers Day" value={formData.holiday_name} onChange={handleChange}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { borderRadius: 3, '& fieldset': { borderColor: '#e2e8f0' } } 
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Holiday Classification</Typography>
                            <TextField
                                fullWidth select name="holiday_type" value={formData.holiday_type} onChange={handleChange}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { borderRadius: 3, '& fieldset': { borderColor: '#e2e8f0' } } 
                                }}
                            >
                                <MenuItem value="PUBLIC">Public / National Holiday</MenuItem>
                                <MenuItem value="COMPANY">Company Observed Closure</MenuItem>
                                <MenuItem value="SPECIAL">Special Event / One-time</MenuItem>
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: 3, border: '1.5px solid #f1f5f9', bgcolor: '#f8fafc' }}>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Active Status</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>Holidays won't apply if disabled.</Typography>
                            </Box>
                            <Switch checked={formData.is_active === 1} onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))} color="success" />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={handleCloseDialog} sx={{ fontWeight: 700, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            bgcolor: 'primary.main', fontWeight: 700, px: 4, borderRadius: 2.5, textTransform: 'none',
                            boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`, '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        {editingHoliday ? "Save Changes" : "Confirm Entry"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HolidayCalendar;
